<template>
  <v-card
    :elevation="previewMode ? 0 : 1"
    class="d-flex flex-column align-center full-width pgm-dashboard-container wallet-container"
    :class="{ 'pa-4': !isMobile, 'pa-2': isMobile, 'rounded-lg': !previewMode }"
    :ripple="false"
    :flat="previewMode"
  >
    <!-- POINTS SECTION -->
    <template v-if="shouldShowPoints">
      <div class="d-flex justify-space-between align-center full-width mb-3">
        <span class="work-break mt-1 text-left">
          Available to spend:
          <v-progress-circular
            v-if="loadingPointsBalance"
            indeterminate
            color="white"
            width="2"
            size="25"
            class="ml-2"
          ></v-progress-circular>
          <span v-else class="word-break available to-spend"
            >{{ computedAvailableToSpendPoints }}
          </span>
        </span>
      </div>
      <v-card
        :key="-3"
        class="mt-2 d-flex justify-space-between align-center rounded list-slide-item border"
        :class="{
          'px-2 py-2': isMobile,
          'px-3 py-3': !isMobile,
        }"
        width="100%"
        flat
      >
        <div class="d-flex align-center text-left">
          <v-icon class="mr-2 rounded wallet-icon dark-green-background">mdi-wallet </v-icon>
          <span class="word-break ml-1">My funds</span>
        </div>
        <span v-if="!loadingPointsBalance">{{ computedPointsBalance }}</span>
        <div v-else>
          <v-progress-circular indeterminate color="brandCyan" width="2"></v-progress-circular>
        </div>
      </v-card>
    </template>

    <!-- Spacer between sections in combined mode -->
    <div v-if="shouldShowPoints && shouldShowCash" class="mt-6 full-width"></div>

    <!-- CASH SECTION -->
    <template v-if="shouldShowCash">
      <div class="d-flex justify-space-between align-center full-width mb-3">
        <span class="work-break mt-1 text-left">
          Available to spend:
          <v-progress-circular
            v-if="loadingBalance"
            indeterminate
            color="white"
            width="2"
            size="25"
            class="ml-2"
          ></v-progress-circular>
          <span v-else class="word-break available to-spend">{{ computedAvailableToSpend }} </span>
        </span>
      </div>
      <v-card
        :key="-2"
        class="mt-2 d-flex justify-space-between align-center rounded list-slide-item border"
        :class="{
          'px-2 py-2': isMobile,
          'px-3 py-3': !isMobile,
        }"
        width="100%"
        flat
      >
        <div class="d-flex align-center text-left">
          <v-icon class="mr-2 rounded wallet-icon dark-green-background">mdi-wallet </v-icon>
          <span class="word-break ml-1">My funds</span>
        </div>
        <span v-if="!loadingBalance">
          {{ computedBalance }}
        </span>
        <div v-else>
          <v-progress-circular indeterminate color="brandCyan" width="2"></v-progress-circular>
        </div>
      </v-card>
    </template>
    <v-card
      v-if="provisionalPayment && !previewMode"
      class="mt-3 rounded border"
      :class="{ 'px-2 py-2': isMobile, 'px-3 py-3': !isMobile }"
      width="100%"
      flat
    >
      <div class="d-flex justify-space-between align-center">
        <div class="d-flex align-center align-text-left">
          <v-icon
            class="mr-2 border-radius wallet-icon"
            :class="{
              'error-background': provisionalPayment.expiring,
              'dark-green-background': !provisionalPayment.expiring,
            }"
            >{{ provisionalPayment.icon }}</v-icon
          >
          <div class="d-flex flex-column justify-center text-left" style="min-width: 100px">
            <span class="word-break mx-1">{{
              provisionalPayment.classification == "PER_DIEM" && !provisionalPayment.description
                ? "Per diem balance"
                : provisionalPayment.nonCash
                ? provisionalPayment.displayName
                : provisionalPayment.description
            }}</span>
          </div>
        </div>
        <span class="text-right word-break" style="max-width: 40%; min-width: 75px">
          {{
            formatCurrency(
              provisionalPayment.balance,
              !provisionalPayment.nonCash,
              provisionalPayment.unitSingular,
              provisionalPayment.unitPlural
            )
          }}
        </span>
      </div>
    </v-card>
    <v-btn
      color="#379f0a"
      class="mt-5 mb-2 white--text"
      depressed
      rounded
      :to="previewMode ? null : '/wallet'"
    >
      Go to wallet
    </v-btn>
  </v-card>
</template>
<script>
import { mapState } from "vuex";
import currency from "currency.js";
import moment from "moment";
import { rewardsOverrideAward } from "@/shared_data/data";
import isOne10PointsEnabled from "@/mixins/one10Points/isOne10PointsEnabled";
import isPaymentCardEnabled from "@/mixins/one10Points/isPaymentCardEnabled";
import WalletService from "@/services/WalletService";

export default {
  name: "Wallet",
  mixins: [isOne10PointsEnabled, isPaymentCardEnabled],
  props: {
    config: {
      type: Object,
      required: true,

      // Developer note:
      // This schema is for readability - it doesn't actually restrict anything on the Vue side
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingPointsBalance: false,
      rewardsPointsBalance: null,
    };
  },
  mounted() {
    if (!this.previewMode && this.shouldShowPoints) {
      this.fetchParticipantBalance();
    }
  },
  methods: {
    async fetchParticipantBalance() {
      if (!this.userProfile?.userId) return;

      this.loadingPointsBalance = true;
      try {
        const balance = await WalletService.getRewardsUserBalanceV2(this.userProfile.userId);
        this.rewardsPointsBalance = balance;
      } catch (error) {
        console.error("Failed to fetch participant balance from rewards service:", error);
        // Fallback to 0 if rewards service fails
        this.rewardsPointsBalance = 0;
      } finally {
        this.loadingPointsBalance = false;
      }
    },
    formatCurrency(amount, cash = true, unitSingular = "unit", unitPlural = "units") {
      return (
        currency(amount, { symbol: cash ? "$" : "" }).format() +
        (cash ? "" : ` ${amount == 1 ? unitSingular : unitPlural}`)
      );
    },
    countdownMillis(date) {
      var now = moment();
      var localTime = moment(date).utc();
      let dur = moment.duration(localTime - now);
      return dur;
    },
    countdown(date) {
      // console.log(date);
      var now = moment();
      var localTime = moment(date).utc();
      let dur = moment.duration(localTime - now);
      if (dur.years() != 0) {
        return dur.years() + (dur.years() == 1 ? " year" : " years");
      }
      if (dur.months() != 0) {
        return dur.months() + (dur.months() == 1 ? " month" : " months");
      }
      if (dur.days() != 0) {
        return dur.days() + (dur.days() == 1 ? " day" : " days");
      }
      // console.log(dur.hours());
      if (dur.hours() != 0) {
        return dur.hours() + (dur.hours() == 1 ? " hour" : " hours");
      }
      if (dur.minutes() != 0) {
        return dur.minutes() + (dur.minutes() == 1 ? " minute" : " minutes");
      }
    },
  },
  computed: {
    ...mapState([
      "walletProfile",
      "balances",
      "cashBalance",
      "pointsBalance",
      "loadingBalance",
      "userProfile",
      "featureFlags",
      "globalClientId",
    ]),
    computedAvailableToSpend: {
      cache: false,
      get: function () {
        if (this.previewMode) return "$250.00";
        var bigTotal = currency(0);

        var paymentRules =
          (this.walletProfile?.paymentRules || [])?.filter((x) => x.displayBalanceInWallet) || [];

        bigTotal = paymentRules.reduce((accumulator, currentValue) => {
          return accumulator.add(currentValue.balance);
        }, currency(0));

        bigTotal = bigTotal.add(this.cashBalance || 0);

        return currency(bigTotal).format();
      },
    },
    computedBalance: {
      cache: false,
      get: function () {
        if (this.previewMode) return "$250.00";
        return currency(this.cashBalance || 0).format();
      },
    },
    provisionalPayment() {
      return (this.walletProfile?.paymentRules || [])
        ?.filter((x) => x.displayBalanceInWallet && x.expiring)
        ?.sort((a, b) => this.countdownMillis(a.endDate) - this.countdownMillis(b.endDate))
        ?.concat(
          (this.walletProfile?.paymentRules || [])
            ?.filter((x) => x.displayBalanceInWallet && !x.expiring)
            ?.sort((a, b) => this.countdownMillis(b.startDate) - this.countdownMillis(a.startDate))
        )
        ?.concat(this.nonCashBalances)?.[0];
    },
    shouldShowCash() {
      const clientId = this.previewMode ? this.globalClientId : undefined;
      return this.isPaymentCardEnabled(clientId);
    },
    shouldShowPoints() {
      const clientId = this.previewMode ? this.globalClientId : undefined;
      return this.isOne10PointsEnabled(clientId);
    },
    computedAvailableToSpendPoints() {
      if (this.previewMode) return "120 " + rewardsOverrideAward.unitPlural;
      const points = this.rewardsPointsBalance ?? this.pointsBalance ?? 0;
      const unit =
        points === 1 ? rewardsOverrideAward.unitSingular : rewardsOverrideAward.unitPlural;
      return `${currency(points, { symbol: "", precision: 0 }).format()} ${unit}`;
    },
    computedPointsBalance() {
      if (this.previewMode) return "120 " + rewardsOverrideAward.unitPluralAbbreviation;
      const points = this.rewardsPointsBalance ?? this.pointsBalance ?? 0;
      const abbrev =
        points === 1
          ? rewardsOverrideAward.unitSingularAbbreviation
          : rewardsOverrideAward.unitPluralAbbreviation;
      return `${currency(points, { symbol: "", precision: 0 }).format()} ${abbrev}`;
    },
  },
};
</script>
<style scoped>
.wallet-container {
  width: 100%;
  height: auto;
  font-size: 22px;

  /* Gradient background
  background: rgb(162, 215, 109);
  background: linear-gradient(0deg, #379f0a 0%, #8fd75a); */

  font-size: 1.4em;
}

.wallet-icon {
  color: #fff;
  padding: 6px;
  border-radius: 4px;
  font-size: 1.3em;
}

.dark-green-background {
  background: #379f0a;
}
.error-background {
  background: var(--v-error-base);
}

/* Removes the grey shadow when clicking a card */
.v-card--link:before {
  background: none;
}
.border {
  border: 1px solid #379f0a;
}
</style>
